import { z } from 'zod';

// API response wrappers (generic — cannot be expressed as zod schemas)
export interface IScimListResponse<T> {
	result: T[];
}

export interface IScimSingleResponse<T> {
	result: T;
}

// Users
export const ScimEmailSchema = z.looseObject({
	primary: z.boolean(),
	type: z.string().nullable(),
	value: z.string().nullable(),
});

export const ScimUserNameSchema = z.looseObject({
	formatted: z.string().nullable(),
	familyName: z.string().nullable(),
	givenName: z.string().nullable(),
});

export const ScimUserSchema = z.looseObject({
	id: z.string(),
	userName: z.string().nullable(),
	displayName: z.string().nullable(),
	active: z.boolean(),
	name: ScimUserNameSchema.nullable(),
	emails: z.array(ScimEmailSchema).nullable(),
	iamUserId: z.string(),
	externalId: z.string().nullable(),
	organizationId: z.string().nullable(),
	accountId: z.string().nullable(),
	createdAt: z.number().nullable(),
	createdBy: z.string().nullable(),
	updatedAt: z.number().nullable(),
	updatedBy: z.string().nullable(),
	version: z.number(),
});

export type IScimEmail = z.infer<typeof ScimEmailSchema>;
export type IScimUser = z.infer<typeof ScimUserSchema>;
export type IScimUserDetail = IScimUser;

// Groups
export const ScimGroupMemberSchema = z.looseObject({
	ref: z.string().nullable(),
	userId: z.string(),
	display: z.string().nullable().optional(),
});

export const ScimGroupSchema = z.looseObject({
	id: z.string(),
	displayName: z.string().nullable(),
	members: z.array(ScimGroupMemberSchema).nullable(),
	externalId: z.string().nullable(),
	organizationId: z.string().nullable(),
	accountId: z.string().nullable(),
	createdAt: z.number().nullable(),
	createdBy: z.string().nullable(),
	updatedAt: z.number().nullable(),
	updatedBy: z.string().nullable(),
	version: z.number(),
});

export type IScimGroupMember = z.infer<typeof ScimGroupMemberSchema>;
export type IScimGroup = z.infer<typeof ScimGroupSchema>;
export type IScimGroupDetail = IScimGroup;

// Policy Maps
export const PolicyMapSchema = z.looseObject({
	id: z.string(),
	groupExternalId: z.string().nullable(),
	policyId: z.string(),
	externalId: z.string().nullable(),
	organizationId: z.string().nullable(),
	accountId: z.string().nullable(),
	createdAt: z.number().nullable(),
	createdBy: z.string().nullable(),
	updatedAt: z.number().nullable(),
	updatedBy: z.string().nullable(),
	version: z.number(),
});

export type IPolicyMap = z.infer<typeof PolicyMapSchema>;
export type IPolicyMapDetail = IPolicyMap;

// Create Policy Map request body
export const CreatePolicyMapSchema = z.object({
	organizationId: z.string(),
	accountId: z.string(),
	groupExternalId: z.string(),
	policyId: z.string(),
});

export type ICreatePolicyMap = z.infer<typeof CreatePolicyMapSchema>;

// Token
export const ScimTokenSchema = z.looseObject({
	token: z.string(),
});

export type IScimToken = z.infer<typeof ScimTokenSchema>;

// Policy (from /iam/policies)
export const PolicySchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	description: z.string().nullable().optional(),
	type: z.string().optional(),
	permissions: z.array(z.string()).nullable().optional(),
	organizationId: z.string().nullable().optional(),
	accountId: z.string().nullable().optional(),
});

export type IPolicy = z.infer<typeof PolicySchema>;

// Service (from /iam/services)
export const ServiceSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	description: z.string().nullable().optional(),
});

export type IService = z.infer<typeof ServiceSchema>;

// Permission (from /iam/services/{serviceId}/permissions)
export const PermissionSchema = z.looseObject({
	id: z.string(),
	name: z.string(),
	description: z.string().nullable().optional(),
	type: z.string(),
	serviceId: z.string(),
});

export type IPermission = z.infer<typeof PermissionSchema>;

// Create Policy request body
export const CreatePolicySchema = z.object({
	name: z.string(),
	description: z.string().nullable().optional(),
	permissions: z.array(z.string()).nullable().optional(),
});

export type ICreatePolicy = z.infer<typeof CreatePolicySchema>;
